import React, { useState, useMemo } from 'react';
import { publications } from '../data/publications';
import { Search, ExternalLink, FileText, BookOpen } from 'lucide-react';
import './Publications.css';

const years = Object.keys(publications).sort((a, b) => b - a);

function Publications() {
  const [search, setSearch] = useState('');
  const [activeYear, setActiveYear] = useState(null);

  const filtered = useMemo(() => {
    const result = {};
    const q = search.toLowerCase();
    
    for (const year of years) {
      const matches = publications[year].filter(pub =>
        pub.title.toLowerCase().includes(q) ||
        pub.authors.toLowerCase().includes(q) ||
        pub.journal.toLowerCase().includes(q)
      );
      if (matches.length > 0) result[year] = matches;
    }
    return result;
  }, [search]);

  const filteredYears = Object.keys(filtered).sort((a, b) => b - a);
  const totalCount = filteredYears.reduce((s, y) => s + filtered[y].length, 0);

  return (
    <div className="publications-page">
      <div className="container">
        <section className="page-hero">
          <h1>Publications</h1>
          <p className="subtitle">
            Peer-reviewed papers and preprints from our research group.
          </p>
        </section>

        {/* Search and stats */}
        <div className="pub-toolbar">
          <div className="pub-search">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by title, author, or journal..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="pub-stats">
            <BookOpen size={16} />
            <span>{totalCount} publication{totalCount !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Year filters */}
        <div className="year-pills">
          <button
            className={`year-pill ${activeYear === null ? 'active' : ''}`}
            onClick={() => setActiveYear(null)}
          >
            All
          </button>
          {years.map(y => (
            <button
              key={y}
              className={`year-pill ${activeYear === y ? 'active' : ''}`}
              onClick={() => setActiveYear(activeYear === y ? null : y)}
            >
              {y}
            </button>
          ))}
        </div>

        {/* Publication list */}
        <div className="pub-list">
          {filteredYears
            .filter(y => activeYear === null || y === activeYear)
            .map(year => (
              <div className="pub-year-group" key={year}>
                <h2 className="pub-year-label">{year}</h2>
                <div className="pub-items">
                  {filtered[year].map((pub, i) => (
                    <article className="pub-item" key={i}>
                      <div className="pub-item-content">
                        <h3 className="pub-title">{pub.title}</h3>
                        <p className="pub-authors">{pub.authors}</p>
                        <p className="pub-journal">{pub.journal}</p>
                      </div>
                      {pub.links && pub.links.length > 0 && (
                        <div className="pub-links">
                          {pub.links.map((link, j) => (
                            <a
                              key={j}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="pub-link"
                              title={link.label}
                            >
                              {link.label.toLowerCase().includes('pdf') ? (
                                <FileText size={14} />
                              ) : (
                                <ExternalLink size={14} />
                              )}
                              <span>{link.label}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            ))}
        </div>

        {filteredYears.length === 0 && (
          <div className="pub-empty">
            <p>No publications match your search.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Publications;
